
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface RealtimeConfig {
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  schema?: string;
  filter?: string;
}

export interface EntityRealtimeConfig {
  entityType: string;
  entityId: string;
  tables: RealtimeConfig[];
}

export interface CollectionRealtimeConfig {
  collectionType: string;
  tables: RealtimeConfig[];
  filters?: Record<string, any>;
}

export interface RealtimeEventHandler {
  onInsert?: (payload: any) => void;
  onUpdate?: (payload: any) => void;
  onDelete?: (payload: any) => void;
  onError?: (error: any) => void;
}

class UniversalRealtimeService {
  private channels: Map<string, RealtimeChannel> = new Map();
  private connectionStatus: Map<string, 'connecting' | 'connected' | 'error' | 'disconnected'> = new Map();
  private retryAttempts: Map<string, number> = new Map();
  private maxRetries = 3;
  private retryDelay = 1000;

  /**
   * Subscribe to real-time updates for an entity
   */
  subscribeToEntity(
    config: EntityRealtimeConfig,
    handlers: RealtimeEventHandler
  ): () => void {
    const channelId = `entity-${config.entityType}-${config.entityId}`;
    return this.createSubscription(channelId, config.tables, handlers);
  }

  /**
   * Subscribe to real-time updates for a collection
   */
  subscribeToCollection(
    config: CollectionRealtimeConfig,
    handlers: RealtimeEventHandler
  ): () => void {
    const channelId = `collection-${config.collectionType}-${JSON.stringify(config.filters || {})}`;
    return this.createSubscription(channelId, config.tables, handlers);
  }

  /**
   * Subscribe to custom real-time configuration
   */
  subscribeToCustom(
    channelId: string,
    configs: RealtimeConfig[],
    handlers: RealtimeEventHandler
  ): () => void {
    return this.createSubscription(channelId, configs, handlers);
  }

  private createSubscription(
    channelId: string,
    configs: RealtimeConfig[],
    handlers: RealtimeEventHandler
  ): () => void {
    // Clean up existing channel if it exists
    this.cleanup(channelId);

    console.log(`[RealtimeService] Creating subscription: ${channelId}`);
    this.connectionStatus.set(channelId, 'connecting');

    const channel = supabase.channel(channelId);

    // Set up listeners for each configuration
    configs.forEach(config => {
      // Fix: Use the correct parameters structure for postgres_changes
      const changeConfig: any = {
        event: config.event,
        schema: config.schema || 'public',
        table: config.table
      };

      // Add filter if provided
      if (config.filter) {
        changeConfig.filter = config.filter;
      }

      channel.on(
        'postgres_changes',
        changeConfig,
        (payload) => {
          console.log(`[RealtimeService] Event received for ${channelId}:`, payload);
          
          try {
            switch (payload.eventType) {
              case 'INSERT':
                handlers.onInsert?.(payload);
                break;
              case 'UPDATE':
                handlers.onUpdate?.(payload);
                break;
              case 'DELETE':
                handlers.onDelete?.(payload);
                break;
            }
          } catch (error) {
            console.error(`[RealtimeService] Handler error for ${channelId}:`, error);
            handlers.onError?.(error);
          }
        }
      );
    });

    // Subscribe and handle connection status
    channel.subscribe((status) => {
      console.log(`[RealtimeService] Status for ${channelId}:`, status);
      
      if (status === 'SUBSCRIBED') {
        this.connectionStatus.set(channelId, 'connected');
        this.retryAttempts.set(channelId, 0);
      } else if (status === 'CHANNEL_ERROR') {
        this.connectionStatus.set(channelId, 'error');
        this.handleConnectionError(channelId, configs, handlers);
      }
    });

    this.channels.set(channelId, channel);

    // Return cleanup function
    return () => this.cleanup(channelId);
  }

  private handleConnectionError(
    channelId: string,
    configs: RealtimeConfig[],
    handlers: RealtimeEventHandler
  ): void {
    const attempts = this.retryAttempts.get(channelId) || 0;
    
    if (attempts < this.maxRetries) {
      console.log(`[RealtimeService] Retrying connection for ${channelId} (attempt ${attempts + 1})`);
      this.retryAttempts.set(channelId, attempts + 1);
      
      setTimeout(() => {
        this.createSubscription(channelId, configs, handlers);
      }, this.retryDelay * Math.pow(2, attempts)); // Exponential backoff
    } else {
      console.error(`[RealtimeService] Max retries exceeded for ${channelId}`);
      handlers.onError?.(new Error(`Connection failed after ${this.maxRetries} attempts`));
    }
  }

  private cleanup(channelId: string): void {
    const channel = this.channels.get(channelId);
    if (channel) {
      console.log(`[RealtimeService] Cleaning up channel: ${channelId}`);
      supabase.removeChannel(channel);
      this.channels.delete(channelId);
      this.connectionStatus.delete(channelId);
      this.retryAttempts.delete(channelId);
    }
  }

  /**
   * Get connection status for a channel
   */
  getConnectionStatus(channelId: string): string {
    return this.connectionStatus.get(channelId) || 'disconnected';
  }

  /**
   * Get all active channels
   */
  getActiveChannels(): string[] {
    return Array.from(this.channels.keys());
  }

  /**
   * Cleanup all channels
   */
  cleanupAll(): void {
    this.channels.forEach((_, channelId) => {
      this.cleanup(channelId);
    });
  }
}

export const realtimeService = new UniversalRealtimeService();
